import * as React from "react";
import { cn } from "./cn";

/**
 * LoadingDots — the "…" of a "Loading messages…" that is still thinking. Three
 * dots fading in and out in turn, so a wait never looks like a page that has
 * simply stopped.
 *
 * Put it where the ellipsis would go: `Loading messages<LoadingDots />`. The
 * dots take the current text colour, so they suit any surface.
 *
 * The three delays are written out in full because Tailwind only keeps class
 * names it can read literally in the source.
 */
const DELAYS = [
  "animate-dot-blink",
  "animate-dot-blink [animation-delay:200ms]",
  "animate-dot-blink [animation-delay:400ms]",
] as const;

export type LoadingDotsProps = React.HTMLAttributes<HTMLSpanElement>;

export const LoadingDots = React.forwardRef<HTMLSpanElement, LoadingDotsProps>(
  ({ className, ...props }, ref) => (
    <span
      ref={ref}
      // Read out as a plain "…" rather than as three separate full stops.
      aria-label="…"
      role="img"
      className={cn("inline-flex items-end gap-0.5 pb-[0.15em] align-baseline", className)}
      {...props}
    >
      {DELAYS.map((delay) => (
        <span
          key={delay}
          aria-hidden="true"
          className={cn("h-[0.28em] w-[0.28em] rounded-full bg-current", delay)}
        />
      ))}
    </span>
  ),
);
LoadingDots.displayName = "LoadingDots";
